/**
 * Sous-onglets d'un écran — **des liens, pas des boutons**.
 *
 * Employé par la Bibliothèque (modèles · aliments · recettes). Les Réglages,
 * eux, emploient un sommaire et non des onglets : cinq volets ne tiennent pas
 * en une rangée sur 393 px, et ils n'ont pas la même nature — voir
 * `screens/settings/SettingsScreen.tsx`.
 *
 * 🔴 **Des liens, parce que la section est une route.** Un jeu de boutons
 * pilotant un `useState` aurait le même rendu et trois défauts : la section
 * disparaîtrait au changement d'onglet (donc la mémoire de navigation n'aurait
 * rien à retenir), le retour par glissement natif ne la traverserait pas
 * (§6.1), et l'adresse ne serait pas partageable.
 *
 * ⚠️ `replace` plutôt qu'un empilement : passer d'« Aliments » à « Recettes »
 * n'est pas une descente dans la hiérarchie, c'est un déplacement latéral.
 * Sans lui, revenir en arrière depuis la bibliothèque ferait remonter une à une
 * toutes les sections visitées avant de quitter enfin l'onglet.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * REFONTE « NOURRI »
 * ══════════════════════════════════════════════════════════════════════════
 *
 * C'est le contrôle segmenté du prototype (`.seg-btn` / `.seg-btn.active`).
 *
 * 🔴 **Les classes de `Button` sont posées à la main sur un `<Link>`, et c'est
 * volontaire.** `Button` rend un `<button>` ; ici il faut une ancre, sans quoi
 * on perd l'`href`, la navigation native et le geste de retour. Reprendre ses
 * classes plutôt que redéclarer un style de segment garde **une seule
 * définition** de ce à quoi ressemble un bouton du système : le jour où le
 * rayon ou l'encre d'un `secondary` change, les onglets suivent sans qu'on y
 * pense. La correspondance est celle documentée dans `Button.tsx` —
 * `.seg-btn` = `secondary` + `rounded`, `.seg-btn.active` = `selected`.
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router';

export interface SectionTab {
  /** Segment d'URL. `null` pour la section par défaut, servie par la racine. */
  section: string | null;
  label: string;
}

export function SectionTabs({
  base,
  tabs,
  current,
  ariaLabel,
}: {
  /** Racine de l'onglet, sans barre oblique finale — « /bibliotheque ». */
  base: string;
  tabs: readonly SectionTab[];
  /** Section active, `null` pour la section par défaut. */
  current: string | null;
  ariaLabel: string;
}) {
  const rangee = useRef<HTMLElement | null>(null);

  /*
   * 🔴 **Amener l'onglet actif dans la zone visible — complément obligatoire du
   * correctif `flex: 0 0 auto`** (V2 §2.6, étape 4).
   *
   * Trouvé en **regardant la capture, pas la mesure** : les quatre libellés ne
   * débordaient plus de leur boîte, la rangée glissait comme prévu, et tous les
   * contrôles étaient verts — mais en arrivant sur `/bibliotheque/exercices`,
   * la rangée restait à `scrollLeft: 0` et l'onglet **actif** était coupé par
   * le bord droit. Une rangée qui glisse sans se positionner montre donc
   * l'onglet où l'on n'est pas.
   *
   * ⚠️ **`scrollLeft` posé à la main, jamais `scrollIntoView()`.** Ce dernier
   * remonte la chaîne des ancêtres défilables : il aurait aussi bougé
   * `<main>`, c'est-à-dire fait sauter la page au changement de section. Ici on
   * ne touche qu'à cette rangée.
   *
   * ⚠️ `behavior: 'auto'` et non `'smooth'` : au premier rendu il n'y a rien à
   * animer — l'onglet doit être **déjà** là quand l'écran apparaît.
   */
  useEffect(() => {
    const nav = rangee.current;
    if (!nav) return;
    const actif = nav.querySelector('[aria-current="page"]');
    if (!(actif instanceof HTMLElement)) return;

    const debut = actif.offsetLeft;
    const fin = debut + actif.offsetWidth;
    const vue = nav.scrollLeft;
    const bord = vue + nav.clientWidth;

    // Une marge, pour que l'onglet ne soit pas collé au bord : un élément
    // exactement à la limite se lit comme s'il était tronqué.
    const marge = 16;
    if (debut < vue + marge) nav.scrollTo({ left: Math.max(0, debut - marge), behavior: 'auto' });
    else if (fin > bord - marge) nav.scrollTo({ left: fin - nav.clientWidth + marge, behavior: 'auto' });
  }, [current]);

  return (
    <nav aria-label={ariaLabel} className="n-segmented" ref={rangee}>
      {tabs.map((tab) => {
        const active = tab.section === current;
        return (
          <Link
            key={tab.label}
            to={tab.section === null ? base : `${base}/${tab.section}`}
            replace
            aria-current={active ? 'page' : undefined}
            className={`n-btn n-btn--rounded n-btn--${active ? 'selected' : 'secondary'}`}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
